import React, { useState, useEffect } from 'react';
import {
  X,
  Send,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ShieldAlert,
  RefreshCw,
  Mail,
  Hash,
  HelpCircle,
} from 'lucide-react';
import Swal from 'sweetalert2';
import ReconciliationModal from './ReconciliationModal';
import {
  getCampaignById,
  getCampaignDeliveries,
  retryFailedDeliveries,
} from '../../../services/newsNewsletterApi';

export default function CampaignDetailModal({
  isOpen,
  onClose,
  campaign: initialCampaign,
  onCampaignUpdated,
}) {
  const [campaign, setCampaign] = useState(initialCampaign);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [activeTab, setActiveTab] = useState('deliveries'); // 'deliveries' | 'snapshot'
  const [isReconcileOpen, setIsReconcileOpen] = useState(false);

  useEffect(() => {
    if (isOpen && initialCampaign?._id) {
      refreshData();
    }
  }, [isOpen, initialCampaign]);

  const refreshData = async () => {
    setLoading(true);
    try {
      const campRes = await getCampaignById(initialCampaign._id);
      if (campRes?.success) {
        setCampaign(campRes.data);
      }
      const delRes = await getCampaignDeliveries(initialCampaign._id, { limit: 100 });
      if (delRes?.success) {
        setDeliveries(delRes.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement détails campagne:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !campaign) return null;

  const uncertainList = deliveries.filter((d) => d.status === 'UNCERTAIN_NEEDS_RECONCILIATION');
  const hasUncertain = uncertainList.length > 0 || (campaign.stats?.uncertainNeedsReconciliation || 0) > 0;

  const handleRetryFailed = async () => {
    if (hasUncertain) {
      Swal.fire({
        title: 'Relance Bloquée (Conflit 409)',
        text: 'Impossible de relancer : des livraisons sont encore au statut incertain (UNCERTAIN_NEEDS_RECONCILIATION). Veuillez obligatoirement effectuer leur réconciliation administrative d’abord.',
        icon: 'error',
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Relancer les livraisons en échec ?',
      text: 'Le système réexpédiera uniquement les emails FAILED ou SOFT_BOUNCE éligibles sans renvoyer aux destinataires ayant déjà reçu l’email.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, relancer',
      cancelButtonText: 'Annuler',
    });

    if (confirm.isConfirmed) {
      setIsRetrying(true);
      try {
        const res = await retryFailedDeliveries(campaign._id);
        Swal.fire('Relance effectuée', res.message || 'Les livraisons ont été traitées.', 'success');
        await refreshData();
        onCampaignUpdated?.();
      } catch (err) {
        Swal.fire('Erreur Relance', err.message || 'Impossible de relancer les livraisons', 'error');
      } finally {
        setIsRetrying(false);
      }
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDeliveryStatusBadge = (status) => {
    const map = {
      QUEUED: { label: 'En file', class: 'warning' },
      SENDING: { label: 'Envoi en cours', class: 'info' },
      ACCEPTED_BY_SMTP: { label: 'Accepté SMTP (250 OK)', class: 'success' },
      DELIVERED: { label: 'Confirmé Délivré', class: 'success' },
      UNCERTAIN_NEEDS_RECONCILIATION: { label: 'Incertain (À réconcilier)', class: 'warning' },
      SOFT_BOUNCE: { label: 'Rebond Temporaire', class: 'secondary' },
      HARD_BOUNCE: { label: 'Rebond Définitif', class: 'danger' },
      FAILED: { label: 'Échec', class: 'danger' },
      SKIPPED_INELIGIBLE: { label: 'Ignoré (Inéligible)', class: 'neutral' },
    };
    const c = map[status] || { label: status, class: 'neutral' };
    return <span className={`an-badge an-badge-${c.class}`}>{c.label}</span>;
  };

  return (
    <>
      <div className="an-modal-backdrop animate-fade-in" onClick={onClose}>
        <div className="an-modal-card an-modal-xlarge" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="an-modal-header">
            <div className="an-modal-title">
              <span className="an-article-code-pill mr-2">{campaign.campaignCode}</span>
              <span>{campaign.internalName}</span>
            </div>
            <button className="an-modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          {/* Subbar with KPI & Actions */}
          <div className="an-modal-subbar">
            <div className="an-campaign-kpi-grid">
              <div className="an-kpi-mini">
                <span className="label">Statut Global</span>
                <span className={`val an-badge an-badge-${campaign.status?.toLowerCase()}`}>
                  {campaign.status}
                </span>
              </div>
              <div className="an-kpi-mini">
                <span className="label">Total Destinataires</span>
                <span className="val">{campaign.stats?.totalTargeted || 0}</span>
              </div>
              <div className="an-kpi-mini">
                <span className="label">Acceptés SMTP</span>
                <span className="val text-success">{campaign.stats?.acceptedBySmtp || 0}</span>
              </div>
              <div className="an-kpi-mini">
                <span className="label">États Incertains</span>
                <span className="val text-warning font-bold">
                  {campaign.stats?.uncertainNeedsReconciliation || 0}
                </span>
              </div>
              <div className="an-kpi-mini">
                <span className="label">Échecs / Rebonds</span>
                <span className="val text-danger">
                  {(campaign.stats?.failedDeliveries || 0) + (campaign.stats?.hardBounces || 0)}
                </span>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="an-subbar-actions mt-3">
              {hasUncertain && (
                <button
                  type="button"
                  className="an-btn-warning mr-2"
                  onClick={() => setIsReconcileOpen(true)}
                >
                  <ShieldAlert size={16} />
                  <span>Réconcilier les Incertains ({uncertainList.length})</span>
                </button>
              )}

              <button
                type="button"
                className={`an-btn-secondary ${hasUncertain ? 'disabled opacity-50' : ''}`}
                onClick={handleRetryFailed}
                disabled={isRetrying || hasUncertain}
                title={hasUncertain ? 'Réconciliation préalable requise' : 'Relancer les échecs'}
              >
                <RefreshCw size={16} className={isRetrying ? 'spin' : ''} />
                <span>{isRetrying ? 'Relance...' : 'Relancer les Échecs'}</span>
              </button>
            </div>
          </div>

          {/* Modal Navigation */}
          <div className="an-modal-nav-tabs px-4">
            <button
              type="button"
              className={`an-modal-nav-tab ${activeTab === 'deliveries' ? 'active' : ''}`}
              onClick={() => setActiveTab('deliveries')}
            >
              Journal des Livraisons SMTP ({deliveries.length})
            </button>
            <button
              type="button"
              className={`an-modal-nav-tab ${activeTab === 'snapshot' ? 'active' : ''}`}
              onClick={() => setActiveTab('snapshot')}
            >
              Empreinte Cryptographique du Snapshot
            </button>
          </div>

          {/* Modal Body */}
          <div className="an-modal-body an-modal-scrollable">
            {activeTab === 'deliveries' && (
              <div className="an-table-responsive">
                {loading ? (
                  <div className="an-loading-state">Chargement du journal des livraisons...</div>
                ) : deliveries.length === 0 ? (
                  <div className="an-empty-state">Aucun enregistrement de livraison trouvé.</div>
                ) : (
                  <table className="an-table">
                    <thead>
                      <tr>
                        <th>Destinataire</th>
                        <th>Statut SMTP</th>
                        <th>Tentatives</th>
                        <th>Date d'acceptation</th>
                        <th>Détails / Diagnostic</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveries.map((del) => (
                        <tr key={del._id}>
                          <td className="font-bold">{del.recipientEmailSnapshot}</td>
                          <td>{getDeliveryStatusBadge(del.status)}</td>
                          <td>{del.attemptsCount} / {del.maxAttempts || 3}</td>
                          <td>{formatDate(del.sentAt)}</td>
                          <td>
                            {del.errorMessage ? (
                              <span className="text-danger small">{del.errorMessage}</span>
                            ) : del.smtpResponse ? (
                              <span className="text-success small">{del.smtpResponse}</span>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'snapshot' && (
              <div className="an-meta-card mt-2">
                <h4>Figeage du Snapshot de Campagne (Anti-Doublon Absolu)</h4>
                <div className="an-meta-row">
                  <span className="an-meta-label">Total des abonnés figés :</span>
                  <span className="an-meta-value font-bold">
                    {campaign.recipientSnapshot?.count || 0}
                  </span>
                </div>
                <div className="an-meta-row">
                  <span className="an-meta-label">Horodatage de la capture :</span>
                  <span className="an-meta-value">
                    {formatDate(campaign.recipientSnapshot?.capturedAt || campaign.sendStartedAt)}
                  </span>
                </div>
                <div className="an-meta-row">
                  <span className="an-meta-label">Empreinte SHA-256 (Snapshot Hash) :</span>
                  <span className="an-meta-value font-mono small text-break">
                    {campaign.recipientSnapshot?.snapshotHash || 'Non disponible'}
                  </span>
                </div>
                {campaign.idempotencyKey && (
                  <div className="an-meta-row">
                    <span className="an-meta-label">Clé d'idempotence unique :</span>
                    <span className="an-meta-value font-mono small text-primary">
                      {campaign.idempotencyKey}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="an-modal-footer">
            <button type="button" className="an-btn-secondary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>

      {/* Reconciliation Sub-Modal */}
      {isReconcileOpen && (
        <ReconciliationModal
          isOpen={isReconcileOpen}
          onClose={() => setIsReconcileOpen(false)}
          campaign={campaign}
          uncertainDeliveries={uncertainList}
          onReconciled={() => {
            refreshData();
            onCampaignUpdated?.();
          }}
        />
      )}
    </>
  );
}

